"use client";

import "@/app/(public)/shared/tokens/colors.css";
import { motion } from "framer-motion";
import { ErrorCardContent, type ErrorCardProps } from "./ErrorCardContent";

export type { ErrorCardAction, ErrorCardProps } from "./ErrorCardContent";

/**
 * Full-page error state: the {@link ErrorCardContent} card centered over an animated
 * landing-themed background. Used by route `error`/`not-found` boundaries and guards.
 * @param props - Error card content configuration; see {@link ErrorCardProps}.
 * @returns The full-page error screen.
 */
export function ErrorCard(props: ErrorCardProps) {
  // Render
  return (
    <div
      className="min-h-screen bg-landing-primary relative overflow-hidden flex items-center justify-center"
      style={{
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-purple-50/50" />
        <motion.div
          animate={{
            rotate: 360,
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          className="absolute top-20 -left-20 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            rotate: -360,
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          className="absolute bottom-20 -right-20 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-2xl mx-auto p-8"
      >
        <ErrorCardContent {...props} />
      </motion.div>
    </div>
  );
}
